import React, { useCallback, useEffect, useState } from 'react';

import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  permissionSlipApiService,
  PermissionSlipFormSummary,
} from '../../../services/permissionSlipApiService';
import { FormStatus } from '../../../types/permissionSlips';

interface FormsListProps {
  status?: FormStatus;
}

export const FormsList: React.FC<FormsListProps> = ({ status }) => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<PermissionSlipFormSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedForm, setSelectedForm] =
    useState<PermissionSlipFormSummary | null>(null);

  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionSlipApiService.getForms({
        status,
        limit: 100,
      });
      setForms(response.forms);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to load permission slip forms'
      );
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void fetchForms();
  }, [fetchForms]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    form: PermissionSlipFormSummary
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedForm(form);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedForm(null);
  };

  const handleEdit = () => {
    if (selectedForm) {
      navigate(`/permission-slips/edit/${selectedForm.id}`);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedForm && window.confirm('Are you sure you want to delete this form?')) {
      try {
        await permissionSlipApiService.deleteForm(selectedForm.id);
        await fetchForms();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete form');
      }
    }
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredForms = forms.filter(form =>
    form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (form.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <TextField
            placeholder="Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/permission-slips/create')}
          >
            Create New Form
          </Button>
        </Box>
      </Box>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredForms.map((form) => (
          <Card key={form.id}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" component="h3">
                  {form.title}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, form)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {form.description}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={form.status}
                  color={getStatusColor(form.status)}
                  size="small"
                />
                {form.category && (
                  <Chip label={form.category} size="small" variant="outlined" />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                Created {new Date(form.created_at).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: '2-digit', 
                  year: 'numeric' 
                })}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredForms.length === 0 && (
        <Alert severity="info">No permission slip forms found.</Alert>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

    </>
  );
}; 